import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { Box, Paper, TextField, Typography } from "@mui/material";
import { memo } from "react";
import PropTypes from "prop-types";

import { wrapFormikHandleChange } from "../../../../../../infrastructure/utils/formatters";
import { resolveGeoLookupData } from "../checkNewRequestPageUtils";
import LocationLinkCopy from "../../../../../components/map/LocationLinkCopy";
import MapViewer from "../../../../../components/map/MapViewer";
import MapLinkInput from "../../../../../components/shared/MapLinkInput";
import DropdownPicker from "../../../../../components/shared/dropdown";
import { FORM_FIELD_SIZE } from "./initialReviewConstants";
import { CRM_CARD_BODY_SX, CRM_CARD_HEADER_SX, CRM_CARD_SX, CRM_FIELD_GRID, CRM_SECTION_TITLE_SX } from "./initialReviewStyles";

const LocationCard = memo(
  ({
    values,
    data,
    formData,
    fieldDisabled,
    onMakeFormikVariable,
    handleChange,
    setFieldValue,
    handleBlur,
    errors,
    touched,
    distributionCompanies = [],
  }) => {
    const { provinceList, cityList: allCityList } = resolveGeoLookupData(
      formData,
      data
    );

    const cityList = allCityList.filter((x) => {
      const cityProvinceId = x.provinceId;
      const currentProvinceId = values.provinceId ?? values.ProvinceId;
      if (!currentProvinceId) return false;
      return (
        String(cityProvinceId) === String(currentProvinceId) ||
        Number(cityProvinceId) === Number(currentProvinceId)
      );
    });

    const companyList = (distributionCompanies || []).map((c) => {
      const id =
        c.distributionCompanyId ?? c.DistributionCompanyId ?? c.id ?? c.Id;
      const code = c.mehrsunCode ?? c.MehrsunCode ?? "";
      const name = c.name ?? c.Name ?? "";
      return {
        id,
        title: code ? `${name} (${code})` : name,
      };
    });

    return (
      <Paper elevation={0} sx={CRM_CARD_SX}>
        <Box sx={CRM_CARD_HEADER_SX}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationOnOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography sx={CRM_SECTION_TITLE_SX}>موقعیت و آدرس</Typography>
          </Box>
        </Box>
        <Box sx={CRM_CARD_BODY_SX}>
          <Box sx={CRM_FIELD_GRID}>
            <DropdownPicker
              size={FORM_FIELD_SIZE}
              label="استان"
              list={provinceList}
              optionName="title"
              optionValue="id"
              value={values.provinceId ? String(values.provinceId) : ""}
              onChange={(event) => {
                setFieldValue("provinceId", event.target.value);
                setFieldValue("cityId", "");
              }}
              onBlur={() => handleBlur({ target: { name: "provinceId" } })}
              error={errors.provinceId && touched.provinceId}
              helperText={
                errors.provinceId && touched.provinceId && errors.provinceId
              }
              disabled={fieldDisabled}
              fullWidth
            />
            <DropdownPicker
              size={FORM_FIELD_SIZE}
              label="شهرستان"
              list={cityList}
              optionName="cityName"
              optionValue="id"
              value={values.cityId ? String(values.cityId) : ""}
              onChange={(event) => setFieldValue("cityId", event.target.value)}
              onBlur={() => handleBlur({ target: { name: "cityId" } })}
              error={errors.cityId && touched.cityId}
              helperText={errors.cityId && touched.cityId && errors.cityId}
              disabled={fieldDisabled}
              fullWidth
            />
            <DropdownPicker
              size={FORM_FIELD_SIZE}
              label="شرکت توزیع (مهرسان) *"
              list={companyList}
              optionName="title"
              optionValue="id"
              value={
                values.distributionCompanyId
                  ? String(values.distributionCompanyId)
                  : ""
              }
              onChange={(event) =>
                setFieldValue(
                  "distributionCompanyId",
                  event.target.value ? Number(event.target.value) : ""
                )
              }
              onBlur={() =>
                handleBlur({ target: { name: "distributionCompanyId" } })
              }
              error={
                errors.distributionCompanyId && touched.distributionCompanyId
              }
              helperText={
                (errors.distributionCompanyId &&
                  touched.distributionCompanyId &&
                  errors.distributionCompanyId) ||
                "برای ثبت‌نام بعدی در مهرسان ضروری است"
              }
              disabled={fieldDisabled}
              fullWidth
            />
            <Box sx={{ gridColumn: "1 / -1" }}>
              <MapLinkInput
                size={FORM_FIELD_SIZE}
                disabled={fieldDisabled}
                onCoordsResolved={({ xPoint, yPoint }) => {
                  setFieldValue("xPoint", xPoint);
                  setFieldValue("yPoint", yPoint);
                }}
              />
            </Box>
            <TextField
              label="مختصات X"
              type="number"
              size={FORM_FIELD_SIZE}
              onChange={wrapFormikHandleChange(handleChange)}
              {...onMakeFormikVariable("xPoint")}
            />
            <TextField
              label="مختصات Y"
              type="number"
              size={FORM_FIELD_SIZE}
              onChange={wrapFormikHandleChange(handleChange)}
              {...onMakeFormikVariable("yPoint")}
            />
          </Box>

          {values.xPoint && values.yPoint ? (
            <Box sx={{ mt: 1.5, display: "grid", gap: 1.5 }}>
              <LocationLinkCopy
                xPoint={values.xPoint}
                yPoint={values.yPoint}
                size={FORM_FIELD_SIZE}
              />
              <MapViewer
                xPoint={values.xPoint}
                yPoint={values.yPoint}
                label="پیش‌نمایش نقشه"
                dialogTitle="موقعیت روی نقشه"
                showButton
              />
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              مختصات ثبت نشده — نقشه در دسترس نیست.
            </Typography>
          )}

          <TextField
            sx={{ mt: 1.5 }}
            label="آدرس محل نصب"
            multiline
            minRows={2}
            size={FORM_FIELD_SIZE}
            onChange={wrapFormikHandleChange(handleChange)}
            {...onMakeFormikVariable("address")}
          />
        </Box>
      </Paper>
    );
  }
);

LocationCard.displayName = "LocationCard";

LocationCard.propTypes = {
  values: PropTypes.object.isRequired,
  data: PropTypes.object,
  formData: PropTypes.object,
  fieldDisabled: PropTypes.bool,
  onMakeFormikVariable: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  handleBlur: PropTypes.func.isRequired,
  errors: PropTypes.object,
  touched: PropTypes.object,
  distributionCompanies: PropTypes.array,
};

export default LocationCard;
